import Vue from 'vue'
import firebase from 'firebase'
import router from '@/router'
import User from '@/models/User'
import BackendService from './BackendService'
import CompanyService from './CompanyService'
import ProjectService from './ProjectService'
import InviteService from './InviteService'

export default class AuthService extends BackendService {

  public user = Vue.observable({})
  private companyService: CompanyService = new CompanyService()
  private projectService: ProjectService = new ProjectService()
  private inviteService: InviteService = new InviteService()

  getUser() {
    return this.user;
  }

  setUser(data: any) {
    this.user = data as User;
  }

  async fetchUser(uid: string) {
    return await firebase.firestore()
      .collection('users')
      .doc(uid)
      .get()
      .then((doc: any) => {
        this.setUser(doc.data())
        return doc.data();
      })
      .catch((error: any) => {
        console.error('Error in fetchUser', error)
      })
  }

  async createNewAccount(form: any) {
    try {
      if (!form.email || !form.password || !form.firstName || !form.lastName) {
        throw Error('Mandatory fields not filled');
      }
      // create firebase auth and user account
      const firebaseUser: any = await this.authCreateEmailAccount(form.email, form.password)
      await this.createUser(firebaseUser.user.uid, form.email, form.firstName, form.lastName)

      return firebaseUser;
    } catch (error) {
      console.error('Error in createNewAccount: ' + error)
      return null
    }
  }

  async checkInvites(email: string) {
    return await this.$db
      .collection('invites')
      .where('email', '==', email)
      .get()
      .then((snap: any) => {
        const arr: any = []
        snap.forEach((doc: any) => {
          const data = doc.data();
          data.id = doc.ref.id;
          data.added = doc.data().added ? doc.data().added.toDate() : null
          arr.push(data)
        })
        return arr;
      })
      .catch((error: any) => {
        console.error('Error in checkInvite', error);
        return false;
      })
  }

  async acceptCompanyInvite(invite: any, companyId: string, userId: string, firstName: string, lastName: string, email: string) {
    return await this.inviteService.acceptInvite(invite, companyId, userId, firstName, lastName, email)
  }

  /*
  async acceptCompanyInvite (invite: any, companyId: string, userId: string, firstName: string, lastName: string, email: string) {
    return await this.companyService.addCompanyUser(companyId, userId, firstName, lastName, email, 'ACTIVE', false)
      .then(async () => {
        //await this.projectService.addUserToDefaultProject(companyId, userId, firstName, lastName, email, 'ACTIVE', false)

        await this.deleteInvite(invite.id)
      })
      .catch((error) => {
        console.error('Error in acceptCompanyInvite', error);
      })
  }

  async deleteInvite (inviteId: string) {
    await firebase.firestore()
      .collection('invites')
      .doc(inviteId)
      .delete();
  }
  */

  async initCompanyAndProject(userId: string, email: string, form: any) {
    // create company
    const companyId = await this.companyService.createCompany(userId, form.companyName, form.companyNumber)
    if (companyId) {
      await this.companyService.addCompanyUser(companyId, userId, form.firstName, form.lastName, email, 'ACTIVE', true)
      await this.projectService.addProjectUserByCompanyId(companyId, userId, form)
    }

    // create default project
    const dt = new Date()
    const project: any = {
      name: form.companyName,
      ownerId: userId,
      companyId: companyId,
      added: dt,
      updated: dt,
      default: true,
    }
    const projectId = await this.projectService.createProject(project)
    if (projectId) {
      await this.projectService.addProjectUser(projectId, userId, form.firstName, form.lastName, email, 'ACTIVE', true)
    }
  }

  async createUser(uid: string, email: string, firstName: string, lastName: string) {
    const dt = new Date();
    await firebase.firestore()
      .collection('users')
      .doc(uid)
      .set({
        id: uid,
        email,
        firstName,
        lastName,
        added: dt,
        updated: dt,
        emailVerified: false,
      })
      .catch((error: any) => {
        console.error('Error in createUser', error)
      })
  }

  async updateUser(uid: string, firstName: string, lastName: string) {
    const dt = new Date();
    await firebase.firestore()
      .collection('users')
      .doc(uid)
      .update({
        firstName,
        lastName,
        updated: new Date(),
      })
      .catch((error: any) => {
        console.error('Error in updateUser', error)
      })
  }

  async authCreateEmailAccount(email: string, password: string) {
    return await firebase.auth()
      .createUserWithEmailAndPassword(email, password)
      .then((res) => {
        return res
      })
      .catch(function (error) {
        Vue.notify({
          group: 'base',
          type: 'error',
          title: 'Ooops, something went wrong',
          text: error.message,
        })
      });

  }

  async authSendResetPassword(email: string) {
    return await firebase.auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        return true;
      })
      .catch(function (error) {
        throw error;
      });
  }

  async authSignInWithEmail(email: string, password: string) {
    await firebase.auth()
      .signInWithEmailAndPassword(email, password)
      .then(() => {
        localStorage.removeItem("activeProjectId");
        router.replace({ name: 'dashboard' })
      })
      .catch(function (error) {
        let err = 'Something went wrong, try again!'
        if (error.code === 'auth/user-not-found') {
          err = "Are you sure that's the right e-mail? Try again!"
        }
        if (error.code === 'auth/wrong-password') {
          err = "Did you forget your password? Try again!"
        }
        Vue.notify({
          group: 'base',
          type: 'error',
          title: 'Ooops',
          text: err,
        })
      });
  }

  async updateUserSignedInInfo(uid: string) {
    await firebase.firestore()
      .collection('users')
      .doc(uid)
      .update({
        lastSignedIn: firebase.firestore.FieldValue.serverTimestamp(),
        numberOfLogins: firebase.firestore.FieldValue.increment(1),
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
      .catch((error: any) => {
        console.error('Error in updateUserSignedInInfo', error)
      })
  }

  async authSignOut() {
    await firebase.auth()
      .signOut()
      .then(function () {
        localStorage.removeItem("activeProjectId");
        router.replace({ name: 'signin' }).catch(() => { console.log('') });
      }).catch(function (error) {
        // An error happened.
      });
  }

}